import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import saveAs from 'file-saver';
import { Gender, SearchDTO, SearchType } from 'src/app/pages/models/analys-resultat';
import { StandardNamnService } from 'src/app/pages/standard-namn/standard-namn.service';
import { formatBigNumber } from 'src/app/shared/helpers/string-utils/string-utils';
import { FOHM, GLASS, SMEs, SWEDRES, ageIntervalStandard } from 'src/app/shared/models/constants';
import { StandardNamn } from 'src/app/shared/models/standard-namn';
import { AnalysresultatService } from '../analysresultat.service';
import { SearchFormService } from './search.form.service';
import { GlobalStateService } from 'src/app/shared/services/global-state.service';
import { SessionService } from 'src/app/auth/services/session.service';
import { AnvandareService } from '../../anvandare/anvandare.service';

const START_YEAR = 2009;
const END_YEAR = new Date().getFullYear();
type YearItem = { label: number; disabled: boolean };
@Component({
    selector: 'app-search-form',
    templateUrl: './search-form.component.html',
    styleUrl: './search-form.component.scss',
    standalone: false
})
export class SearchFormComponent implements OnInit {
  @Input() searchType!: SearchType;
  private fb = inject(FormBuilder);
  private analysresultatService = inject(AnalysresultatService);
  private searchFormService = inject(SearchFormService);
  protected standardNamnService = inject(StandardNamnService);
  private destroy = inject(DestroyRef);
  protected globalStateService = inject(GlobalStateService);
  protected sessionService = inject(SessionService);
  protected anvandareService = inject(AnvandareService);

  protected defaultDate = new Date();
  protected maxStartDate = new Date();
  protected minEndDate = new Date();
  protected maxEndDate = new Date();
  protected searchTypes = SearchType;
  protected SMEs = SMEs;
  protected ageIntervalStandardOptions = ageIntervalStandard;
  protected results!: any;
  protected currentAgeIntervals: string[] = [];
  protected currentSearchDTO: SearchDTO = {};
  protected showSkeleton = false;
  protected showCalc = false;
  protected searchableYears: YearItem[] = [];

  protected searchForm = this.fb.group({
    species: [''],
    sampleYear: { label: 0, disabled: false } as YearItem,
    analysis: [''],
    sampleMaterial: [''],
    antibiotics: [''],
    labCodes: ['' as any],
    ageIntervalStandard: null,
    ageSpan: null,
    gender: [''],
    sampleDateStart: null,
    sampleDateEnd: null,
    externalLabChoice: null,
  });

  ngOnInit(): void {
    this.standardNamnService.load();
    this.searchForm.controls.ageSpan.disable();
    this.searchForm.controls.sampleDateStart.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((date: any) => {
        if (!date) {
          this.resetFormDates();
        } else {
          this.minEndDate = new Date(date);
          this.searchForm.controls.sampleYear.patchValue(
            this.searchableYears.find((year) => year.label === new Date(date).getFullYear()) as YearItem,
          );
          this.searchForm.controls.sampleYear.markAsDirty();
          this.searchableYears.forEach((year) => {
            if (year.label != date.label) {
              year.disabled = true;
            }
          });
        }
      });
    this.searchForm.controls.sampleYear.valueChanges.pipe(takeUntilDestroyed(this.destroy)).subscribe((date: any) => {
      if (!date) {
        return;
      }
      this.defaultDate = new Date(date.label, 0, 1);
      if (date.label === new Date().getFullYear()) {
        this.maxStartDate = new Date();
        this.maxEndDate = new Date();
      } else {
        this.maxStartDate = new Date(date.label, 11, 30);
        this.maxEndDate = new Date(date.label, 11, 31);
      }
    });
    this.searchForm.controls.ageIntervalStandard.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((interval: any) => {
        this.searchForm.controls.ageSpan.enable();

        switch (interval) {
          case 'GLASS':
            this.currentAgeIntervals = GLASS;
            break;
          case 'SWEDRES':
            this.currentAgeIntervals = SWEDRES;
            break;
          case 'FOHM':
            this.currentAgeIntervals = FOHM;
            break;
          default:
            break;
        }
      });
    this.searchForm.controls.externalLabChoice.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((choice: any) => {
        if (choice === 'all') {
          this.searchForm.controls.labCodes.reset();
        } else {
          this.anvandareService
            .getAllOrganisations()
            .pipe(takeUntilDestroyed(this.destroy))
            .subscribe((orgs) => {
              const kml = orgs.find((org) => org.id === this.sessionService.getOrgId())?.labKod ?? null;
              if (kml) {
                this.searchForm.controls.labCodes.patchValue([{ name: kml }]);
              }
            });
        }
      });

    [...Array(END_YEAR - START_YEAR + 1)].forEach((x, i) => {
      this.searchableYears.unshift({ label: START_YEAR + i, disabled: false });
    });
  }

  protected setCurrentSearchDTO(searchDTO: SearchDTO) {
    this.currentSearchDTO = {
      ...searchDTO,
    };
  }
  performSearch = () => {
    this.showCalc = false;
    this.results = undefined;
    this.showSkeleton = true;
    this.currentSearchDTO = {};
    const species = (this.searchForm.controls.species.value as unknown as StandardNamn)?.standardNamn;
    const searchDTO: SearchDTO = {
      smittamne: species
        ? [species]
        : Array.isArray(this.searchForm.controls.species.value)
          ? this.searchForm.controls.species.value.map((x) => x.standardNamn)
          : undefined,
      fullYear: this.searchForm.controls.sampleYear.value?.label ?? undefined,
      analys: Array.isArray(this.searchForm.controls.analysis.value)
        ? this.searchForm.controls.analysis.value.map((x) => x.standardNamn)
        : undefined,
      provMaterial: Array.isArray(this.searchForm.controls.sampleMaterial.value)
        ? this.searchForm.controls.sampleMaterial.value.map((x) => x.standardNamn)
        : undefined,
      labb: Array.isArray(this.searchForm.controls.labCodes.value)
        ? this.searchForm.controls.labCodes.value.map((x) => x.name)
        : undefined,
      antibiotics:
        Array.isArray(this.searchForm.controls.antibiotics.value) &&
        this.searchForm.controls.antibiotics.value.length > 0
          ? this.searchForm.controls.antibiotics.value.map((x) => x.standardNamn)
          : undefined,
      gender:
        Array.isArray(this.searchForm.controls.gender.value) && this.searchForm.controls.gender.value.length > 0
          ? this.searchForm.controls.gender.value.map((gender) => this.mapGender(gender))
          : undefined,
      sampleDateStart: this.searchForm.controls.sampleDateStart.value ?? undefined,
      sampleDateEnd: this.searchForm.controls.sampleDateEnd.value ?? undefined,
      ageSpan: this.searchForm.controls.ageSpan.value
        ? this.mapAgeSpan(this.searchForm.controls.ageSpan.value)
        : undefined,
      positive: this.searchType === SearchType.NAMNARDATA ? false : true,
      ageIntervalStandard: this.searchForm.controls.ageIntervalStandard.value ?? undefined,
      searchType: this.searchType,
      countSearch: this.searchType === SearchType.NAMNARDATA ? ['FYND', 'FYNDTYPE'] : ['FYND'],
    };
    this.setCurrentSearchDTO(searchDTO);
    this.searchFormService.saveLatestSearchParams(JSON.stringify(searchDTO));

    this.analysresultatService.countSearch<SearchDTO>(searchDTO).subscribe((x: SearchDTO) => {
      this.showSkeleton = false;
      this.results = x;
    });
  };

  private mapGender(gender: string) {
    switch (gender) {
      case 'Man':
        return Gender.MAN;
      case 'Kvinna':
        return Gender.KVINNA;
      case 'Okänt':
        return Gender.OKANT;
      default:
        return Gender.EJ_ANGIVET;
    }
  }

  private mapAgeSpan(ageSpan: string) {
    if (ageSpan.includes('<')) {
      return [0, 0];
    }
    if (ageSpan.split('-')[1] === '') {
      return [Number(ageSpan.split('-')[0]), 999];
    }
    return ageSpan.split('-').map((x) => Number(x));
  }

  protected resetForm() {
    this.results = undefined;
    this.showCalc = false;
    this.searchForm.reset();
    this.searchForm.controls.ageSpan.disable();
    this.resetFormDates();
  }

  private resetFormDates() {
    this.defaultDate = new Date();
    this.maxStartDate = new Date();
    this.maxEndDate = new Date();
    this.minEndDate = new Date();
    this.searchForm.controls.sampleYear.reset();
    this.searchableYears.forEach((year) => (year.disabled = false));
  }

  protected calc() {
    this.showCalc = true;
  }

  protected searchAndGroupByProv() {
    window.open('search-results', '_blank');
  }

  protected getRowDataCsv() {
    this.globalStateService.setCsvDownloading(true);
    if (this.searchType !== SearchType.NAMNARDATA) {
      this.analysresultatService.getSpecificRowDataCsv<Blob>(this.currentSearchDTO).subscribe((tableData: Blob) => {
        saveAs(tableData, `SVEBAR_Provlista_${new Date().toLocaleString('sv-SE').replace(' ', 'T')}.csv`);
        this.globalStateService.setCsvDownloading(false);
      });
    }
    if (this.searchType === SearchType.NAMNARDATA) {
      this.analysresultatService.getNamnarDataCsv<Blob>(this.currentSearchDTO).subscribe((tableData: Blob) => {
        saveAs(tableData, `SVEBAR_Nämnardata_Provlista_${new Date().toLocaleString('sv-SE').replace(' ', 'T')}.csv`);
        this.globalStateService.setCsvDownloading(false);
      });
    }
  }

  protected getCalcCsv() {
    this.globalStateService.setCsvDownloading(true);

    this.analysresultatService.getCalcCsv<Blob>(this.currentSearchDTO).subscribe((tableData: Blob) => {
      saveAs(tableData, `SVEBAR_Antibiotikaresistens_${new Date().toLocaleString('sv-SE').replace(' ', 'T')}.csv`);
      this.globalStateService.setCsvDownloading(false);
    });
  }
  protected formatBigNumber(number: number) {
    return !number ? '' : formatBigNumber(String(number));
  }
}
